interface TruckSVGProps {
  className?: string
  variant?: 'dark' | 'light'
}

const TruckSVG = ({ className, variant = 'dark' }: TruckSVGProps) => {
  const body = variant === 'dark' ? '#111111' : '#e8e8e8'
  const cab = variant === 'dark' ? '#1a1a1a' : '#d0d0d0'
  const outline = '#f5c518'
  const wheel = variant === 'dark' ? '#222222' : '#333333'
  const text = variant === 'dark' ? '#ffffff' : '#0a0a0a'

  return (
    <svg
      className={className}
      viewBox="0 0 920 210"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Vision International semi-truck"
    >
      {/* ─── TRAILER ─── */}
      <rect x="10" y="42" width="570" height="128" rx="3" fill={body} stroke={outline} strokeWidth="1.5" />

      {/* Trailer vertical ribs */}
      {[60, 110, 160, 210, 260, 310, 360, 410, 460, 510].map((x) => (
        <line key={x} x1={x} y1="42" x2={x} y2="170" stroke={outline} strokeWidth="0.4" strokeOpacity="0.35" />
      ))}

      {/* Trailer top stripe */}
      <rect x="10" y="42" width="570" height="14" rx="3" fill={outline} opacity="0.12" />

      {/* "VISION INTERNATIONAL" text on trailer */}
      <text
        x="295"
        y="116"
        textAnchor="middle"
        fill={text}
        fontFamily="'Barlow Condensed', sans-serif"
        fontSize="22"
        fontWeight="900"
        letterSpacing="6"
        opacity="0.9"
      >
        VISION INTERNATIONAL
      </text>
      <text
        x="295"
        y="136"
        textAnchor="middle"
        fill={outline}
        fontFamily="'Barlow', sans-serif"
        fontSize="9"
        fontWeight="700"
        letterSpacing="5"
        opacity="0.8"
      >
        FREIGHT WITHOUT LIMITS
      </text>

      {/* Trailer rear door lines */}
      <line x1="568" y1="42" x2="568" y2="170" stroke={outline} strokeWidth="2" />
      <line x1="580" y1="42" x2="580" y2="170" stroke={outline} strokeWidth="1" strokeOpacity="0.3" />

      {/* ─── 5TH WHEEL / COUPLING ─── */}
      <rect x="570" y="145" width="35" height="12" rx="2" fill="#333" />

      {/* ─── CAB ─── */}
      {/* Cab body */}
      <rect x="598" y="60" width="195" height="110" rx="5" fill={cab} stroke={outline} strokeWidth="1.5" />

      {/* Cab roof / spoiler */}
      <rect x="600" y="42" width="185" height="22" rx="4" fill={cab} stroke={outline} strokeWidth="1" />
      <rect x="605" y="44" width="175" height="8" rx="2" fill={outline} opacity="0.15" />

      {/* Windshield */}
      <rect x="612" y="68" width="100" height="68" rx="4" fill={outline} fillOpacity="0.08" stroke={outline} strokeWidth="1" strokeOpacity="0.5" />
      {/* Windshield glare */}
      <line x1="618" y1="72" x2="640" y2="130" stroke="white" strokeWidth="1" strokeOpacity="0.15" />

      {/* Door */}
      <rect x="718" y="68" width="65" height="90" rx="3" fill="none" stroke={outline} strokeWidth="0.8" strokeOpacity="0.4" />
      {/* Door handle */}
      <rect x="750" y="108" width="20" height="4" rx="2" fill={outline} opacity="0.6" />

      {/* Side window */}
      <rect x="722" y="72" width="55" height="36" rx="3" fill={outline} fillOpacity="0.06" stroke={outline} strokeWidth="0.6" strokeOpacity="0.4" />

      {/* Front bumper */}
      <rect x="788" y="145" width="12" height="25" rx="2" fill={outline} opacity="0.9" />

      {/* Headlight */}
      <rect x="790" y="92" width="10" height="18" rx="2" fill={outline} opacity="0.95" />
      <rect x="790" y="92" width="10" height="18" rx="2" fill="white" opacity="0.3" />

      {/* Grill */}
      <rect x="789" y="115" width="10" height="30" rx="1" fill="#333" stroke={outline} strokeWidth="0.8" />
      {[118, 122, 126, 130, 134, 138, 142].map((y) => (
        <line key={y} x1="789" y1={y} x2="799" y2={y} stroke={outline} strokeWidth="0.5" strokeOpacity="0.6" />
      ))}

      {/* Right mirror */}
      <rect x="797" y="76" width="12" height="18" rx="2" fill="#333" stroke={outline} strokeWidth="0.8" />

      {/* Exhaust stacks */}
      <rect x="776" y="20" width="9" height="50" rx="3" fill="#2a2a2a" stroke={outline} strokeWidth="0.8" />
      <rect x="789" y="24" width="7" height="40" rx="3" fill="#2a2a2a" stroke={outline} strokeWidth="0.8" />
      {/* Smoke */}
      <circle cx="780" cy="17" r="5" fill="#555" opacity="0.25" />
      <circle cx="784" cy="10" r="4" fill="#555" opacity="0.15" />

      {/* Step */}
      <rect x="600" y="152" width="45" height="6" rx="2" fill={outline} opacity="0.5" />
      <rect x="600" y="160" width="35" height="5" rx="2" fill={outline} opacity="0.3" />

      {/* ─── WHEELS ─── */}
      {/* Trailer rear axle 1 */}
      <circle cx="110" cy="182" r="26" fill={wheel} stroke={outline} strokeWidth="2" />
      <circle cx="110" cy="182" r="14" fill="#333" stroke={outline} strokeWidth="1" />
      <circle cx="110" cy="182" r="4" fill={outline} />
      {/* Hub bolts */}
      {[0, 60, 120, 180, 240, 300].map((angle) => (
        <circle
          key={angle}
          cx={110 + 9 * Math.cos((angle * Math.PI) / 180)}
          cy={182 + 9 * Math.sin((angle * Math.PI) / 180)}
          r="2"
          fill={outline}
          opacity="0.7"
        />
      ))}

      {/* Trailer rear axle 2 */}
      <circle cx="185" cy="182" r="26" fill={wheel} stroke={outline} strokeWidth="2" />
      <circle cx="185" cy="182" r="14" fill="#333" stroke={outline} strokeWidth="1" />
      <circle cx="185" cy="182" r="4" fill={outline} />
      {[0, 60, 120, 180, 240, 300].map((angle) => (
        <circle
          key={angle}
          cx={185 + 9 * Math.cos((angle * Math.PI) / 180)}
          cy={182 + 9 * Math.sin((angle * Math.PI) / 180)}
          r="2"
          fill={outline}
          opacity="0.7"
        />
      ))}

      {/* Drive axle 1 */}
      <circle cx="625" cy="182" r="26" fill={wheel} stroke={outline} strokeWidth="2" />
      <circle cx="625" cy="182" r="14" fill="#333" stroke={outline} strokeWidth="1" />
      <circle cx="625" cy="182" r="4" fill={outline} />
      {[0, 60, 120, 180, 240, 300].map((angle) => (
        <circle
          key={angle}
          cx={625 + 9 * Math.cos((angle * Math.PI) / 180)}
          cy={182 + 9 * Math.sin((angle * Math.PI) / 180)}
          r="2"
          fill={outline}
          opacity="0.7"
        />
      ))}

      {/* Drive axle 2 */}
      <circle cx="695" cy="182" r="26" fill={wheel} stroke={outline} strokeWidth="2" />
      <circle cx="695" cy="182" r="14" fill="#333" stroke={outline} strokeWidth="1" />
      <circle cx="695" cy="182" r="4" fill={outline} />
      {[0, 60, 120, 180, 240, 300].map((angle) => (
        <circle
          key={angle}
          cx={695 + 9 * Math.cos((angle * Math.PI) / 180)}
          cy={182 + 9 * Math.sin((angle * Math.PI) / 180)}
          r="2"
          fill={outline}
          opacity="0.7"
        />
      ))}

      {/* Steer axle */}
      <circle cx="768" cy="182" r="24" fill={wheel} stroke={outline} strokeWidth="2" />
      <circle cx="768" cy="182" r="13" fill="#333" stroke={outline} strokeWidth="1" />
      <circle cx="768" cy="182" r="4" fill={outline} />
      {[0, 60, 120, 180, 240, 300].map((angle) => (
        <circle
          key={angle}
          cx={768 + 8 * Math.cos((angle * Math.PI) / 180)}
          cy={182 + 8 * Math.sin((angle * Math.PI) / 180)}
          r="1.8"
          fill={outline}
          opacity="0.7"
        />
      ))}

      {/* Road line */}
      <line x1="0" y1="208" x2="920" y2="208" stroke={outline} strokeWidth="0.8" strokeOpacity="0.3" />
    </svg>
  )
}

export default TruckSVG
